import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'components/shared/Modal/Modal';
import { searchDevicesV2 } from 'redux/actions';
import { LOADING_CIRCLE } from 'common/utils/constants';
import { getDefaultSwVersions } from 'apis/swManagerApis/GetDefaultSwVersions';
import { getProtectedDevices } from 'apis/swManagerApis/GetProtectedDevices';
import { getFactoryProtectedDevices } from 'apis/swManagerApis/GetFactoryProtectedDevices';
import { Status } from 'common/utils/constants';
import WarningBox from 'components/shared/WarningBox/WarningBox';
import { TWO_HOP_ALIGNMENT_SCREEN_INDEXES } from '../utils';
import { getFlattenedDeviceData } from 'apis/SearchDevices';
import { DEFAULT_PIVOT_IDS_OBJ } from 'common/utils/constants';
import CommissionAddDevicesScreen from 'components/features/Commissioning/CommissionAddDevicesScreen/CommissionAddDevicesScreen';

import './TwoHopPivotSelection.css';

const PIVOT1_TITLE = 'Pivot 1';
const PIVOT1_SUBTITLE = 'This is a Pivot system facing towards gNB';
const PIVOT2_TITLE = 'Pivot 2';
const PIVOT2_SUBTITLE = 'Pivot system facing toward desired coverage area';
const DUPLICATE_ERROR_MESSAGE = 'Same device cannot be selected.';
const HBF_REQUIRED_ERROR_MESSAGE = 'Pivot 1 must have a HBF_SERVICE device';

export const TwoHopPivotSelection = (props) => {
  const [hopOneDeviceIds, setHopOneDeviceIds] = useState(['', '', '']);
  const [hopTwoDeviceIds, setHopTwoDeviceIds] = useState(['', '', '']);

  const [defaultVersions, setDefaultVersions] = useState([]);
  const [defaultVerionsRequestStatus, setDefaultVerionsRequestStatus] = useState(Status.NONE);
  const [protectedDevices, setProtectedDevices] = useState([]);
  const [protectedDevicesRequestStatus, setProtectedDevicesRequestStatus] = useState(Status.NONE);
  const [factoryProtectedDevices, setFactoryProtectedDevices] = useState([]);
  const [factoryProtectedDevicesRequestStatus, setFactoryProtectedDevicesRequestStatus] = useState(Status.NONE);

  // set error on initial load since all inputs are empty
  const [pivot1Error, setPivot1Error] = useState(true);
  const [pivot2Error, setPivot2Error] = useState(true);
  // error message for duplicate and HBF checks
  const [twoHopError, setTwoHopError] = useState('');

  const dispatch = useDispatch();

  useEffect(() => {
    setDefaultVerionsRequestStatus(Status.PENDING);
    setProtectedDevicesRequestStatus(Status.PENDING);
    setFactoryProtectedDevicesRequestStatus(Status.PENDING);

    getDefaultSwVersions()
      .then((res) => {
        if (!res.some((value) => value.deviceType === 'Pivot5G')) {
          setDefaultVerionsRequestStatus(Status.FAILURE);
        } else {
          setDefaultVersions(res);
          setDefaultVerionsRequestStatus(Status.SUCCESS);
        }
      })
      .catch((e) => setDefaultVerionsRequestStatus(Status.FAILURE));

    getProtectedDevices()
      .then((res) => {
        setProtectedDevicesRequestStatus(Status.SUCCESS);

        const translatedRes = Array.isArray(res) ? res : res.values;
        setProtectedDevices(translatedRes || []);
      })
      .catch((e) => setProtectedDevicesRequestStatus(Status.FAILURE));

    getFactoryProtectedDevices()
      .then((res) => {
        setFactoryProtectedDevicesRequestStatus(Status.SUCCESS);
        const translatedRes = Array.isArray(res) ? res : res.values;
        setFactoryProtectedDevices(translatedRes || []);
      })
      .catch((e) => setFactoryProtectedDevicesRequestStatus(Status.FAILURE));
  }, []);

  const handleNext = () => {
    const hopOneDevicesArray = hopOneDeviceIds.filter(
      (deviceId) => deviceId !== '' && deviceId !== null && deviceId !== undefined
    );
    const hopTwoDevicesArray = hopTwoDeviceIds.filter(
      (deviceId) => deviceId !== '' && deviceId !== null && deviceId !== undefined
    );
    const deviceIdArray = [...hopOneDevicesArray, ...hopTwoDevicesArray];

    const searchBody = {
      size: 10000,
      filters: { deviceId: deviceIdArray },
      coalesceBundles: false
    };

    dispatch(
      searchDevicesV2(
        searchBody,
        'v2',
        (result) => {
          const flattenDeviceDict = JSON.parse(JSON.stringify(DEFAULT_PIVOT_IDS_OBJ));
          const newSelectedDevicesOne = [{}, {}, {}];
          const newSelectedDevicesTwo = [{}, {}, {}];
          const deviceDict = {};

          result.devices.forEach((d) => {
            deviceDict[d.deviceId] = d;
          });

          result.devices.forEach((device) => {
            const deviceId = device.deviceId;
            const flattenDevice = getFlattenedDeviceData(device);
            if (deviceId === hopOneDeviceIds[0]) {
              flattenDeviceDict['hop1donor1'] = flattenDevice;
              newSelectedDevicesOne[0] = flattenDevice;
            } else if (deviceId === hopOneDeviceIds[1]) {
              flattenDeviceDict['hop1service1'] = flattenDevice;
              newSelectedDevicesOne[1] = flattenDevice;
            } else if (deviceId === hopOneDeviceIds[2]) {
              flattenDeviceDict['hop1service2'] = flattenDevice;
              newSelectedDevicesOne[2] = flattenDevice;
            } else if (deviceId === hopTwoDeviceIds[0]) {
              flattenDeviceDict['hop2donor1'] = flattenDevice;
              newSelectedDevicesTwo[0] = flattenDevice;
            } else if (deviceId === hopTwoDeviceIds[1]) {
              flattenDeviceDict['hop2service1'] = flattenDevice;
              newSelectedDevicesTwo[1] = flattenDevice;
            } else if (deviceId === hopTwoDeviceIds[2]) {
              flattenDeviceDict['hop2service2'] = flattenDevice;
              newSelectedDevicesTwo[2] = flattenDevice;
            }
          });
          props.sendPivots({
            hop1donor1: hopOneDeviceIds[0],
            hop1service1: hopOneDeviceIds[1],
            hop1service2: hopOneDeviceIds[2],
            hop2donor1: hopTwoDeviceIds[0],
            hop2service1: hopTwoDeviceIds[1],
            hop2service2: hopTwoDeviceIds[2]
          });

          props.sendTwinDict(deviceDict);

          props.setSelectedDevicesOne(newSelectedDevicesOne);
          props.setSelectedDevicesTwo(newSelectedDevicesTwo);

          if (flattenDeviceDict['hop1service1']?.productType !== 'HBF_SERVICE') {
            setTwoHopError(HBF_REQUIRED_ERROR_MESSAGE);
          } else {
            props.onNext(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.PIVOT_SELECTION);
          }
        },
        () => {}
      )
    );
  };

  const hasDuplicateDevice = () => {
    return hopOneDeviceIds.some((id) => id !== '' && hopTwoDeviceIds.includes(id));
  };

  const getTwoHopErrorMessage = () => {
    if (hasDuplicateDevice()) return DUPLICATE_ERROR_MESSAGE;

    return twoHopError;
  };

  const getApiRequestsStatus = () => {
    const requests = [defaultVerionsRequestStatus, protectedDevicesRequestStatus, factoryProtectedDevicesRequestStatus];
    if (requests.some((r) => r === Status.FAILURE)) {
      return Status.FAILURE;
    }
    if (requests.some((r) => r === Status.PENDING)) {
      return Status.PENDING;
    }

    return Status.NONE;
  };

  let overlay = null;
  if (getApiRequestsStatus() === Status.PENDING) {
    overlay = <div className="LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  } else if (getApiRequestsStatus() === Status.FAILURE) {
    overlay = (
      <div className="RequestErrorContainer">
        Fail to retrieve required data for two hop alignment. Please try again later.
      </div>
    );
  }

  return (
    <Modal
      animateX={false}
      global
      close={props.onClose}
      noPadding
      displayFlex={true}
      overflow={true}
      height={'fit-content'}
      width={1080}
    >
      <div className="HopWizardPivotSelection">
        {overlay}
        <div className="HopWizardPivotSelection_Header">
          <div className="HopWizardPivotSelectionTitle">Two-Hop Selection</div>
          <div className="HopWizardPivotSelectionSubTitle">Select Pivot systems to run alignment process</div>
          <WarningBox
            warningMessages={[
              'It is CRITICAL that all devices are identified in each Two-Hop Pivot Bundle. Please verify that the DeviceIDs are valid for each Bundle. It is highly recommended to review Quickbase to enter in and verify all DeviceIDs.'
            ]}
          />
        </div>
        <div className="HopWizardPivotSelection_Content">
          <CommissionAddDevicesScreen
            onClose={props.onClose}
            onNext={props.onNext}
            setRecommBundleId={() => {}}
            twoHop={true}
            title={PIVOT1_TITLE}
            subtitle={PIVOT1_SUBTITLE}
            updateDeviceIds={(deviceIds) => setHopOneDeviceIds(deviceIds)}
            updateError={(hasError) => setPivot1Error(hasError)}
            defaultVersions={defaultVersions}
            protectedDevices={protectedDevices}
            factoryProtectedDevices={factoryProtectedDevices}
            resetError={() => setTwoHopError('')}
          />

          <CommissionAddDevicesScreen
            onClose={props.onClose}
            onNext={props.onNext}
            setRecommBundleId={() => {}}
            twoHop={true}
            title={PIVOT2_TITLE}
            subtitle={PIVOT2_SUBTITLE}
            updateDeviceIds={(deviceIds) => setHopTwoDeviceIds(deviceIds)}
            updateError={(hasError) => setPivot2Error(hasError)}
            defaultVersions={defaultVersions}
            protectedDevices={protectedDevices}
            factoryProtectedDevices={factoryProtectedDevices}
            resetError={() => setTwoHopError('')}
          />
        </div>
        <div className="HopWizardPivotSelectionFooter">
          {!pivot1Error && !pivot2Error && (
            <div className="HopWizardPivotSelectionFooterErrorMessage">{getTwoHopErrorMessage()}</div>
          )}
          <div className="HopWizardPivotSelectionFooterCancel Global_CancelButton" onClick={props.onClose}>
            Cancel
          </div>
          <button
            id="HopWizardPivotSelectionFooterNext"
            className="HopWizardPivotSelectionFooterNext"
            disabled={pivot1Error || pivot2Error || getTwoHopErrorMessage() !== ''}
            onClick={handleNext}
          >
            Next
          </button>
        </div>
      </div>
    </Modal>
  );
};
